import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Badge,
  InputBase,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Category as CategoryIcon,
  Fastfood as FastfoodIcon,
  Payment as PaymentIcon,
  RateReview as ReviewIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  SupportAgent as SupportIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  RestartAlt as ReturnIcon,
  Search as SearchIcon,
  Event as EventIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import axios from '../../api';

const drawerWidth = 264;

// Menu configurations by role
const getMenuSections = (role: string) => {
  const commonMenu = [
    {
      label: '',
      items: [{ text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' }],
    },
  ];

  if (role === 'admin') {
    return [
      ...commonMenu,
      {
        label: 'Marketplace',
        items: [
          { text: 'Surprise Boxes', icon: <InventoryIcon />, path: '/surprise-boxes' },
          { text: 'Orders', icon: <ShoppingCartIcon />, path: '/orders' },
          { text: 'Businesses', icon: <StoreIcon />, path: '/businesses' },
          { text: 'Banquet Halls', icon: <EventIcon />, path: '/banquet-halls' },
          { text: 'Banquet Bookings', icon: <EventIcon />, path: '/banquet-bookings' },
          { text: 'Menu Items', icon: <FastfoodIcon />, path: '/menu-items' },
          { text: 'Categories', icon: <CategoryIcon />, path: '/categories' },
        ],
      },
      {
        label: 'People',
        items: [
          { text: 'Users', icon: <PeopleIcon />, path: '/users' },
          { text: 'Access Management', icon: <SettingsIcon />, path: '/access-management' },
        ],
      },
      {
        label: 'Operations',
        items: [
          { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
          { text: 'Reviews & Reports', icon: <ReviewIcon />, path: '/reviews' },
          { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
          { text: 'Returns & Refunds', icon: <ReturnIcon />, path: '/returns-refunds' },
          { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
          { text: 'Support', icon: <SupportIcon />, path: '/support' },
          { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
        ],
      },
    ];
  }

  if (role === 'merchant') {
    return [
      ...commonMenu,
      {
        label: 'My Business',
        items: [
          { text: 'My Business', icon: <StoreIcon />, path: '/my-business' },
          { text: 'My Boxes', icon: <InventoryIcon />, path: '/surprise-boxes' },
          { text: 'Menu Items', icon: <FastfoodIcon />, path: '/my-menu-items' },
          { text: 'Banquet Halls', icon: <EventIcon />, path: '/my-banquet-halls' },
          { text: 'Banquet Bookings', icon: <EventIcon />, path: '/my-banquet-bookings' },
          { text: 'Orders', icon: <ShoppingCartIcon />, path: '/orders' },
          { text: 'Send Notifications', icon: <NotificationsIcon />, path: '/merchant-notifications' },
        ],
      },
      {
        label: 'Reports',
        items: [
          { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
          { text: 'Reviews', icon: <ReviewIcon />, path: '/reviews' },
          { text: 'Notification History', icon: <NotificationsIcon />, path: '/merchant-notification-history' },
          { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
        ],
      },
    ];
  }

  if (role === 'customer') {
    return [
      ...commonMenu,
      {
        label: 'Shopping',
        items: [
          { text: 'Restaurants', icon: <StorefrontIcon />, path: '/restaurants' },
          { text: 'Browse Boxes', icon: <InventoryIcon />, path: '/surprise-boxes' },
          { text: 'My Cart', icon: <ShoppingCartIcon />, path: '/cart' },
          { text: 'My Orders', icon: <ShoppingCartIcon />, path: '/orders' },
        ],
      },
      {
        label: 'Account',
        items: [
          { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
          { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
        ],
      },
    ];
  }

  if (role === 'delivery') {
    return [
      ...commonMenu,
      {
        label: 'Deliveries',
        items: [
          { text: 'My Deliveries', icon: <ShoppingCartIcon />, path: '/orders' },
        ],
      },
      {
        label: 'Account',
        items: [
          { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
        ],
      },
    ];
  }

  return commonMenu;
};

const ROLE_TITLES: { [key: string]: string } = {
  customer: 'BiteHub Marketplace',
  merchant: 'BiteHub Merchant Portal',
  delivery: 'BiteHub Delivery Portal',
  admin: 'BiteHub Admin Dashboard',
};

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [navQuery, setNavQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const role = user?.role || 'customer';
  const menuSections = useMemo(() => getMenuSections(role), [role]);

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get('/api/notifications/unread-count');
        if (response.data.success) {
          setUnreadCount(response.data.unreadCount);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Update page title based on user role
  useEffect(() => {
    document.title = ROLE_TITLES[role] || 'BiteHub';
  }, [role]);

  // Human-readable name of the current page (for the topbar breadcrumb)
  const pageTitle = useMemo(() => {
    const path = location.pathname;
    const flat = menuSections.flatMap((s) => s.items);
    const exact = flat.find((i) => i.path === path);
    if (exact) return exact.text;
    if (path.startsWith('/orders/')) return 'Order Details';
    const seg = path.split('/').filter(Boolean).pop() || 'Dashboard';
    return seg
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }, [location.pathname, menuSections]);

  // Filter nav items by the sidebar search box
  const filteredSections = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return menuSections;
    return menuSections
      .map((s) => ({ ...s, items: s.items.filter((i) => i.text.toLowerCase().includes(q)) }))
      .filter((s) => s.items.length > 0);
  }, [menuSections, navQuery]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const initials = (user?.name || 'User').charAt(0).toUpperCase();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, height: 64, flexShrink: 0 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #FF7A00 0%, #0B6E4F 100%)',
            transform: 'rotate(45deg)',
            flexShrink: 0,
          }}
        />
        <Typography component="div" sx={{ fontWeight: 800, fontSize: '1.15rem', letterSpacing: '-0.02em' }}>
          <Box component="span" sx={{ color: '#0B6E4F' }}>Bite</Box>
          <Box component="span" sx={{ color: '#FF7A00' }}>Hub</Box>
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 38,
            px: 1.25,
            borderRadius: 2.5,
            bgcolor: '#F1F1EC',
            border: '1px solid transparent',
            transition: 'border-color .15s, background-color .15s',
            '&:focus-within': { bgcolor: '#fff', borderColor: 'secondary.main' },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <InputBase
            value={navQuery}
            onChange={(e) => setNavQuery(e.target.value)}
            placeholder="Search menu"
            sx={{ flex: 1, fontSize: 14, color: 'text.primary' }}
          />
          <Box
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              bgcolor: '#E7E7E1',
              color: 'text.secondary',
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1.4,
            }}
          >
            ⌘K
          </Box>
        </Box>
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 0.5 }}>
        {filteredSections.map((section, si) => (
          <Box key={section.label || `main-${si}`} sx={{ mb: 1.5 }}>
            {section.label && (
              <Typography
                sx={{
                  px: 1.5,
                  mt: 1,
                  mb: 0.5,
                  color: 'text.secondary',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                }}
              >
                {section.label}
              </Typography>
            )}
            {section.items.map((item) => {
              const selected = location.pathname === item.path;
              const badge = item.path === '/notifications' ? unreadCount : 0;
              return (
                <ListItem key={item.text + item.path} disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    selected={selected}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      minHeight: 40,
                      px: 1.5,
                      color: 'text.secondary',
                      '& .MuiListItemIcon-root': { color: 'text.secondary', minWidth: 32 },
                      '& .MuiSvgIcon-root': { fontSize: 20 },
                      '&:hover': { bgcolor: 'action.hover' },
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                        color: 'text.primary',
                        '&:hover': { bgcolor: 'action.selected' },
                        '& .MuiListItemIcon-root': { color: 'text.primary' },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 4,
                          top: 8,
                          bottom: 8,
                          width: 3,
                          borderRadius: 3,
                          backgroundColor: 'secondary.main',
                        },
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      slotProps={{ primary: { sx: { fontWeight: selected ? 500 : 400, fontSize: 14 } } }}
                    />
                    {badge > 0 && (
                      <Box
                        sx={{
                          minWidth: 20,
                          height: 20,
                          px: 0.75,
                          borderRadius: 5,
                          bgcolor: selected ? 'secondary.main' : '#EDEDE7',
                          color: selected ? '#fff' : 'text.secondary',
                          fontSize: 11,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {badge > 99 ? '99+' : badge}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Box>
        ))}
        {filteredSections.length === 0 && (
          <Typography sx={{ px: 2, py: 2, color: 'text.secondary', fontSize: 13 }}>
            No menu items match “{navQuery}”.
          </Typography>
        )}
      </Box>

      {/* Footer: user profile card */}
      <Box sx={{ p: 1.5, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 1,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#FCFCFA',
          }}
        >
          {user?.avatar ? (
            <Box
              component="img"
              src={user.avatar}
              alt={user.name}
              sx={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 15,
                flexShrink: 0,
              }}
            >
              {initials}
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.2 }}>
              {user?.name || 'User'}
            </Typography>
            <Typography noWrap sx={{ color: 'text.secondary', fontSize: 12, textTransform: 'capitalize' }}>
              {role}
            </Typography>
          </Box>
          <Tooltip title="Log out">
            <IconButton size="small" onClick={logout} sx={{ color: 'text.secondary' }}>
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'rgba(246,246,243,0.85)',
          backdropFilter: 'blur(8px)',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, sm: 64 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Breadcrumb */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 14, display: { xs: 'none', sm: 'block' } }}
            >
              {ROLE_TITLES[role]?.replace('BiteHub ', 'BiteHub / ') || 'BiteHub'}
            </Typography>
            <Typography sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>/</Typography>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 15 }}>
              {pageTitle}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Notifications">
              <IconButton onClick={() => navigate('/notifications')} sx={{ color: 'text.secondary' }}>
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            {user?.avatar ? (
              <Box
                component="img"
                src={user.avatar}
                alt={user.name}
                sx={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1px solid', borderColor: 'divider' }}
              />
            ) : (
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                {initials}
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid', borderColor: 'divider' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid', borderColor: 'divider' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, sm: 64 } }} />
        <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 1 } }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
